import React from 'react';
import { ISubmission, ISubmissionVenueData, SubmissionType } from 'btdt-types';

interface IProps {
  submission: ISubmission & { venue?: { name: string; nameEn?: string; address?: string; yearOpened?: number; yearClosed?: number | null; tags?: string[] } };
  onUpdate(updated: ISubmission): void;
}

const fieldLabels: Record<string, string> = {
  name: 'שם המקום',
  nameEn: 'שם באנגלית',
  address: 'כתובת',
  yearOpened: 'שנת פתיחה',
  yearClosed: 'שנת סגירה',
  tags: 'קטגוריות',
  yearsCertain: 'שנים בטוחות',
};

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function SubmissionDetail({ submission, onUpdate }: IProps): React.ReactElement {
  const venue = submission.venue;
  const [venueData, setVenueData] = React.useState<ISubmissionVenueData>(() => {
    const data = { ...submission.venueData };
    if (data.address && data.address.includes(',')) {
      data.address = data.address.split(',')[0].trim();
    }
    return data;
  });
  const [source, setSource] = React.useState(submission.source || '');
  const [photoCredits, setPhotoCredits] = React.useState<string[]>([...(submission.photoCredits || [])]);
  const [photoCaptions, setPhotoCaptions] = React.useState<string[]>([...(submission.photoCaptions || [])]);
  const [enlargedPhoto, setEnlargedPhoto] = React.useState<string | null>(null);
  const [dirty, setDirty] = React.useState(false);

  const editableFields = Object.keys(submission.venueData || {}).filter(
    (key) => key !== 'coordinates' && key !== 'city' && (venueData as any)[key] !== undefined
  );

  const updateField = (key: string, value: string) => {
    setDirty(true);
    setVenueData((prev) => {
      const updated = { ...prev };
      if (key === 'yearOpened' || key === 'yearClosed') {
        (updated as any)[key] = value ? parseInt(value) : undefined;
      } else if (key === 'tags') {
        (updated as any)[key] = value.split(',').map((t) => t.trim()).filter(Boolean);
      } else {
        (updated as any)[key] = value;
      }
      return updated;
    });
  };

  const handleSave = async () => {
    onUpdate({
      ...submission,
      venueData,
      source,
      photoCredits,
      photoCaptions,
    });
    setDirty(false);
  };

  return (
    <div style={{ padding: '12px 16px', background: '#3a3a3a', borderTop: '1px solid #555' }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#aaa' }}>מקור</label>
          <input
            value={source}
            onChange={(e) => { setSource(e.target.value); setDirty(true); }}
            style={{ width: '100%', background: '#2a2a2a', color: '#eee', border: '1px solid #555', borderRadius: 4, padding: '4px 6px', fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 13, color: '#aaa' }}>דוא"ל</label>
          <div style={{ padding: '4px 0', fontSize: 13 }}>{submission.submitterEmail || '—'}</div>
        </div>
      </div>

      {submission.type === SubmissionType.SUGGEST_EDIT && venue && (
        <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: 13 }}>שדה</th>
              <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: 13 }}>ערך נוכחי</th>
              <th style={{ textAlign: 'right', padding: '4px 8px', fontSize: 13 }}>ערך מוצע</th>
            </tr>
          </thead>
          <tbody>
            {editableFields.map((key) => {
              const currentVal = (venue as any)[key];
              const proposedVal = (venueData as any)[key];
              const changed = formatValue(currentVal) !== formatValue(proposedVal);
              return (
                <tr key={key} style={changed ? { background: '#4a4a2a' } : {}}>
                  <td style={{ padding: '4px 8px', fontSize: 13 }}>{fieldLabels[key] || key}</td>
                  <td style={{ padding: '4px 8px', fontSize: 13 }}>{formatValue(currentVal)}</td>
                  <td style={{ padding: '4px 8px' }}>
                    <input
                      value={formatValue(proposedVal) === '—' ? '' : formatValue(proposedVal)}
                      onChange={(e) => updateField(key, e.target.value)}
                      style={{ width: '100%', background: '#2a2a2a', color: '#eee', border: '1px solid #555', borderRadius: 4, padding: '2px 6px', fontSize: 13 }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {submission.type === SubmissionType.NEW_VENUE && (
        <div style={{ marginTop: 8 }}>
          {editableFields.map((key) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
              <label style={{ fontSize: 13, color: '#aaa', minWidth: 100 }}>{fieldLabels[key] || key}</label>
              <input
                value={formatValue((venueData as any)[key]) === '—' ? '' : formatValue((venueData as any)[key])}
                onChange={(e) => updateField(key, e.target.value)}
                style={{ flex: 1, background: '#2a2a2a', color: '#eee', border: '1px solid #555', borderRadius: 4, padding: '2px 6px', fontSize: 13 }}
              />
            </div>
          ))}
        </div>
      )}

      {submission.photoUrls && submission.photoUrls.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <strong style={{ fontSize: 13 }}>תמונות:</strong>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
            {submission.photoUrls.map((url, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 140 }}>
                <img
                  src={url}
                  alt=""
                  style={{ width: 140, height: 100, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                  onClick={() => setEnlargedPhoto(url)}
                />
                <input
                  value={photoCaptions[i] || ''}
                  onChange={(e) => {
                    const updated = [...photoCaptions];
                    updated[i] = e.target.value;
                    setPhotoCaptions(updated);
                    setDirty(true);
                  }}
                  placeholder="תיאור"
                  style={{ width: '100%', background: '#2a2a2a', color: '#eee', border: '1px solid #555', borderRadius: 4, padding: '2px 4px', fontSize: 12 }}
                />
                <input
                  value={photoCredits[i] || ''}
                  onChange={(e) => {
                    const updated = [...photoCredits];
                    updated[i] = e.target.value;
                    setPhotoCredits(updated);
                    setDirty(true);
                  }}
                  placeholder="קרדיט"
                  style={{ width: '100%', background: '#2a2a2a', color: '#eee', border: '1px solid #555', borderRadius: 4, padding: '2px 4px', fontSize: 12 }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {dirty && (
        <button
          onClick={handleSave}
          style={{ marginTop: 12, background: '#336699', color: 'white', border: 'none', borderRadius: 4, padding: '4px 16px', cursor: 'pointer', fontSize: 13 }}
        >
          שמירת שינויים
        </button>
      )}

      {enlargedPhoto && (
        <div
          onClick={() => setEnlargedPhoto(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, cursor: 'pointer',
          }}
        >
          <img src={enlargedPhoto} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}

export default SubmissionDetail;
